import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NavigationStateManager from '../../../helpers/NavigationStateManager';
import { EntityActionsDropdown } from '../EntityActionsDropdown/EntityActionsDropdown';
import type { EntityActionsMenuItem, EntityActionsMenuItemAction } from '../EntityActionsDropdown/EntityActionsDropdown';
import { ReadOnlyContext } from '../grid/ReadOnlyContext';
import styles from './EntityHeaderActions.module.css';

export type EntityHeaderActionsProps = {
  editAction?: EntityActionsMenuItem;
  menuItems: EntityActionsMenuItem[];
  actionsLabel: string;
  className?: string;
  saveReturnUrl?: boolean; // If false, skips saving return URL. Default is true when editAction is provided.
};

export const EntityHeaderActions: React.FC<EntityHeaderActionsProps> = ({
  editAction,
  menuItems,
  actionsLabel,
  className,
  saveReturnUrl
}) => {
  const navigate = useNavigate();
  const isReadOnly = useContext(ReadOnlyContext);

  // Hide editAction entirely for read-only users (it is a write action by nature)
  const effectiveEditAction = isReadOnly ? undefined : editAction as EntityActionsMenuItemAction | undefined;
  
  const containerClassName = className
    ? `${styles.container} ${className}`
    : styles.container;

  const hasEditAction = Boolean(effectiveEditAction);
  
  // Filter out null/undefined menu items to get visible items
  const visibleMenuItems = menuItems.filter(Boolean);
  const hasVisibleMenuItems = visibleMenuItems.length > 0;
  
  // Default saveReturnUrl to true when editAction is provided, unless explicitly set to false
  const shouldSaveReturnUrl = saveReturnUrl !== false && hasEditAction;

  const handleEditClick = (event: React.MouseEvent) => {
    if (!effectiveEditAction || effectiveEditAction.disabled) {
      event.preventDefault();
      return;
    }

    // Save current URL as return URL (default behavior when editAction exists)
    if (shouldSaveReturnUrl) {
      const currentUrl = NavigationStateManager.getCurrentPageUrl();
      NavigationStateManager.setReturnUrl(currentUrl);
    }

    // If linkTo is provided, navigate using React Router
    if (effectiveEditAction.linkTo) {
      event.preventDefault();
      navigate(effectiveEditAction.linkTo);
      return;
    }

    // Otherwise call the onSelect handler
    const maybePromise = effectiveEditAction.onSelect?.();
    
    if (maybePromise && typeof (maybePromise as Promise<unknown>).then === 'function') {
      (maybePromise as Promise<unknown>).catch((error) => {
        console.error('Edit action failed:', error);
      });
    }
  };

  const renderEditButton = () => {
    if (!effectiveEditAction) return null;

    const buttonContent = (
      <>
        {effectiveEditAction.icon && (
          React.isValidElement(effectiveEditAction.icon)
            ? React.cloneElement(effectiveEditAction.icon, {
                className: styles.editButtonIcon,
                'aria-hidden': true
              })
            : effectiveEditAction.icon
        )}
        <span>{effectiveEditAction.label}</span>
      </>
    );

    const buttonClassName = effectiveEditAction.disabled
      ? `${styles.editButton} ${styles.editButtonDisabled}`
      : styles.editButton;

    // Always use button to handle click logic (including saveReturnUrl)
    return (
      <button
        type="button"
        onClick={handleEditClick}
        className={buttonClassName}
        disabled={effectiveEditAction.disabled}
        aria-disabled={effectiveEditAction.disabled || undefined}
      >
        {buttonContent}
      </button>
    );
  };

  return (
    <div className={containerClassName}>
      {renderEditButton()}
      {hasVisibleMenuItems && (
        <EntityActionsDropdown
          triggerLabel={actionsLabel}
          menuItems={visibleMenuItems}
          variant={hasEditAction ? 'secondary' : 'primary'}
        />
      )}
    </div>
  );
};
